import type { PropId } from "./props";
import { DECOR } from "./decor";
import { madeOn, madeShelves, isMade } from "./madeStickers";

/**
 * The catalogue: every sticker in the room, sorted onto shelves.
 *
 * Anything placed in the room but not named on a shelf lands in Trinkets, so
 * a new sticker always shows up somewhere without this list being updated.
 */

export interface Shelf {
  key: string;
  name: string;
  /** a little picture for the tab */
  emoji: string;
  items: PropId[];
}

export const SHELVES: Shelf[] = [
  {
    key: "furniture",
    name: "Beds & dressers",
    emoji: "🛏️",
    items: [
      "bed-iron",
      "dresser",
      "vanity",
      "cabinet-jewelry",
      "radiator_heater_cast_iron_ribbed_white_vintage_sticker",
      "cabinet_china_peach_pink_jadeite_pink_depression_glass_sticker",
      "dresser_five_drawer_light_oak_scalloped_gold_pulls_sticker",
      "vanity_oak_scalloped_round_mirror_sticker",
      "cabinet_apothecary_cream_many_drawers_novelty_knobs_sticker",
      "bed_french_cream_cherry_print_pink_bedding_sticker",
      "dresser_walnut_mid_century_drawers_tapered_legs_sticker",
      "wardrobe_antique_mahogany_mirror_door_drawers_sticker",
    ],
  },
  {
    key: "seating",
    name: "Sofas & chairs",
    emoji: "🛋️",
    items: [
      "armchair-sage",
      "loveseat",
      "chair-vanity",
      "stool_strawberry_red_glazed_sticker",
      "sofa_sectional_chaise_blush_pink_pillows_sticker",
      "sofa_sectional_chaise_slipcover_grey_rolled_arms_sticker",
      "sofa_sectional_chaise_slipcover_grey_rolled_arms_angled_left_sticker",
      "sofa_sectional_chaise_slipcover_grey_rolled_arms_angled_chaise_right_sticker",
      "sofa_sectional_chaise_slipcover_grey_rolled_arms_side_view_sticker",
    ],
  },
  {
    key: "shelving",
    name: "Shelves & rugs",
    emoji: "🧺",
    items: [
      "shelf_rack_garden_three_tier_scrollwork_glass_white_sticker",
      "shelf_wall_geometric_diamond_gold_frame_three_tier_white_sticker",
      "rug_floral_tufted_grey_multicolour_flowers_sticker",
      "shelf_wall_picture_ledge_oak_vinyl_records_hers_mitski_top_sticker",
      "shelf_wall_picture_ledge_oak_vinyl_records_puberty_be_the_cowboy_bottom_sticker",
      "rug_cat_tufted_peach_flowers_sticker",
      "telescope_refractor_tripod_navy_blue_meade_sticker",
      "telescope_refractor_tripod_navy_blue_meade_facing_left_sticker",
    ],
  },
  { key: "trellises", name: "Trellises", emoji: "🌿", items: ["trellis-wisteria", "trellis-ivy"] },
  {
    key: "plants",
    name: "House plants",
    emoji: "🪴",
    items: [
      "vase_cat_pink_ceramic_trailing_plant_sticker",
      "monstera-crystal",
      "dracaena",
      "fiddle-leaf-fig",
      "planter-greek-head",
      "decor-ivy-bottle",
      "bottle_vase_lowell_embossed_pothos_cutting_green_glass_antique_sticker",
      "bottle_vase_pothos_cutting_embossed_green_glass_sticker",
      "planter_hanging_macrame_woven_basket_string_of_pearls_sticker",
      "plant_snake_sansevieria_white_pot_sticker",
      "planter_pot_embossed_floral_medallion_off_white_sticker",
      "planter_pot_hand_painted_purple_flowers_saucer_sticker",
    ],
  },
  {
    key: "plant-shelves",
    name: "Plant shelves",
    emoji: "🌿",
    items: [
      "shelf_wall_long_trailing_pothos_watering_can_column_fairy_lights_sticker",
      "shelf_wall_long_pothos_teacups_string_of_pearls_fairy_lights_sticker",
      "shelf_corner_ivy_green_bottle_teacup_fairy_lights_sticker",
      "shelf_corner_pothos_basket_red_bottle_fairy_lights_sticker",
    ],
  },
  {
    key: "blossoms",
    name: "Blossoms",
    emoji: "🌸",
    items: ["jacaranda", "hydrangea-bush", "lilac-bush", "rose-bush", "arch-roses"],
  },
  {
    key: "wisteria",
    name: "Wisteria",
    emoji: "💜",
    items: ["wisteria-branch-lavender", "wisteria-branch-purple", "wisteria-vine", "wisteria-garland"],
  },
  {
    key: "cats",
    name: "Strawberry & Blueberry",
    emoji: "🐱",
    items: [
      "cat-blueberry-running",
      "cat-blueberry-sitting",
      "cat-blueberry-party",
      "cat-blueberry-monstera",
      "cat-strawberry",
      "cat-strawberry-roses",
      "cat-strawberry-window",
      "kitten-strawberry",
    ],
  },
  {
    key: "more-cats",
    name: "More cats",
    emoji: "🐾",
    items: [
      "cats-birthday",
      "cat-petunia",
      "cats-roses",
      "cats-toilet",
      "kittens-christmas",
      "cat-honeysuckle",
      "honeysuckle-tricycle",
      "honeysuckle-clock",
      "honeysuckle-sewing",
      "honeysuckle-bow",
      "honeysuckle-bow-alt",
    ],
  },
  {
    key: "artwork",
    name: "Art & posters",
    emoji: "🖼️",
    items: [
      "artwork_print_woman_moka_pot_line_black_white_sticker",
      "artwork_framed_magazine_cover_deco_yellow_sticker",
      "poster_lillebaeltsbroen_bridge_denmark_vintage_sticker",
      "poster_danish_composer_pianist_portrait_vintage_sticker",
      "relief-gold-frame",
      "panel-four-seasons",
      "cherub-gilded",
      "plaque-ivory",
      "plaque-rose-wood",
      "plaque-wood-dark",
      "artwork_framed_line_drawing_woman_braids_black_white_sticker",
      "artwork_framed_print_woman_blue_leaves_sticker",
      "artwork_framed_print_red_poppies_pink_sticker",
      "artwork_framed_vogue_cover_art_deco_sticker",
      "artwork_framed_abstract_splatter_painting_blue_green_gold_frame_sticker",
      "artwork_framed_abstract_pour_painting_pink_blue_black_gold_frame_sticker",
      "artwork_print_art_nouveau_girl_crown_green_giantess_sticker",
    ],
  },
  {
    key: "mirrors",
    name: "Mirrors",
    emoji: "🪞",
    items: [
      "mirror-glass-floral",
      "mirror-glass-blue",
      "mirror-glass-tulip",
      "mirror_wall_arch_ornate_gold_vintage_sticker",
      "mirror_wall_oval_ornate_gold_crest_vintage_sticker",
    ],
  },
  {
    key: "suncatchers",
    name: "Suncatchers",
    emoji: "🌈",
    items: [
      "suncatcher_stained_glass_hanging_scalloped_floral_turquoise_sticker",
      "suncatcher_crystal_prisms_gold_moon_sun_hanging_sticker",
      "suncatcher_crystal_hummingbird_prism_hanging_sticker",
      "suncatcher_crystal_rose_quartz_gold_prism_hanging_sticker",
    ],
  },
  {
    key: "lights",
    name: "Hanging lights",
    emoji: "💡",
    items: ["pendant-opal", "pendant-cameo", "pendant-opal-antique", "garland_ivy_vine_fairy_lights_sticker"],
  },
  { key: "people", name: "People", emoji: "👯‍♀️", items: ["fall-table-brea-molly"] },
  { key: "dogs", name: "Dogs", emoji: "🐶", items: ["dog-maggie-frisbee", "dog-charlie-kiddie-pool", "dog-frisbee", "dog-leo-dalmatian"] },
  { key: "goats", name: "Goats", emoji: "🐐", items: ["goat-bambi", "goats-pumpkin-ferdinand"] },
  { key: "birds", name: "Birds", emoji: "🦚", items: ["peacock_bird_standing_trailing_tail_sticker"] },
  { key: "creatures", name: "Creatures", emoji: "🐌", items: ["snail_giant_african_land_shell_sticker", "snail_giant_african_land_shell_facing_left_sticker", "snail_giant_vicious_fanged_spiked_facing_left_sticker", "snail_giant_vicious_fanged_spiked_sticker", "snail_giant_vicious_eating_a_man_sticker"] },
  { key: "pillows", name: "Pillows", emoji: "🎀", items: ["pillow-floral", "pillow-lace", "pillow-bolster"] },
  {
    key: "teacups",
    name: "Teacups",
    emoji: "☕",
    items: [
      "teacups-collection",
      "teacup-rose",
      "teacup-sage",
      "teacup-turquoise",
      "teacup-blush",
      "teacup-blue-gilt",
      "teacup-multicolor",
      "teacup-rosebud",
      "teacup-rosebud-mini",
    ],
  },
  {
    key: "ornaments",
    name: "Ornament balls",
    emoji: "🔮",
    items: [
      "ornament_reflector_rounded_magenta_sticker",
      "ornament_teardrop_frosted_glitter_orange_sticker",
      "ornament_ball_hand_painted_red_flower_silver_sticker",
      "ornament_ball_pink_ribbon_bow_turquoise_sticker",
      "ornament_ball_painted_leaf_red_sticker",
      "ornament_reflector_saucer_silver_sticker",
      "ornament_finial_onion_glitter_stripes_green_silver_large_sticker",
      "ornament_ball_reflector_magenta_large_sticker",
      "ornament_ball_painted_stripes_red_green_silver_sticker",
      "ornament_ball_turquoise_small_sticker",
      "ornament_ball_shiny_purple_sticker",
      "ornament_ball_worn_silver_blue_spots_sticker",
      "ornament_finial_onion_glitter_stripes_green_silver_small_sticker",
      "ornament_ball_reflector_magenta_small_sticker",
    ],
  },
  {
    key: "christmas",
    name: "Christmas trees & figures",
    emoji: "🎄",
    items: [
      "tree_christmas_decorated_presents_sticker",
      "ornament_grape_cluster_purple_sticker",
      "ornament_pinecone_quilted_pink_sticker",
      "ornament_grape_cluster_cobalt_blue_sticker",
      "ornament_pickle_glass_lime_green_sticker",
      "ornament_santa_claus_glass_pink_sticker",
      "ornament_santa_claus_red_pink_ribbon_sticker",
      "ornament_berry_cluster_red_sticker",
      "ornament_strawberry_frosted_coral_red_sticker",
      "ornament_strawberry_small_red_sticker",
      "tree_jadeite_glass_christmas_large_sticker",
      "tree_jadeite_glass_christmas_small_sticker",
    ],
  },
  {
    key: "kitchen",
    name: "Kitchen",
    emoji: "☕",
    items: [
      "coffee_maker_drip_smeg_retro_pink_sticker",
      "espresso_machine_smeg_retro_cream_sticker",
      "microwave_insignia_retro_mint_teal_sticker",
      "stove_range_smeg_victoria_lavender_sticker",
      "mandarins_cuties_organic_mesh_bag_sticker",
      "mandarins_halos_mesh_bag_sticker",
    ],
  },
  {
    key: "books",
    name: "Books",
    emoji: "📚",
    items: [
      "book-tiffany",
      "cookbook_recipes_red_gilt_closed_sticker",
      "book_cookbook_open_recipes_sticker",
      "book_everybodys_story_loyal_rue_hardcover_sticker",
      "book_everybodys_story_loyal_rue_lying_down_sticker",
      "book_the_game_changing_attorney_michael_mogill_sticker",
      "book_the_game_changing_attorney_michael_mogill_hardcover_sticker",
      "book_hot_girl_crochet_rose_svane_sticker",
      "book_hot_girl_crochet_rose_svane_hardcover_sticker",
      "book_world_travel_anthony_bourdain_sticker",
      "book_world_travel_anthony_bourdain_hardcover_sticker",
      "book_hundred_years_war_on_palestine_rashid_khalidi_sticker",
      "book_grc_engineering_for_aws_aj_yawn_sticker",
      "book_madeleine_ludwig_bemelmans_hardcover_sticker",
      "book_no_one_belongs_here_more_than_you_miranda_july_sticker",
      "book_no_one_belongs_here_more_than_you_miranda_july_lying_down_sticker",
      "book_witchy_moon_phases_purple_galaxy_clasp_sticker",
    ],
  },
  {
    key: "dishes",
    name: "Dishes & glassware",
    emoji: "🍽️",
    items: [
      "plate-blue-gold",
      "tray_trinket_porcelain_gilt_medallion_cobalt_blue_sticker",
      "flute_glass_champagne_tall_cobalt_blue_sticker",
      "goblet_glass_wine_aqua_turquoise_sticker",
      "bottle_glass_embossed_decanter_peach_pink_vintage_sticker",
      "decor-strawberry-glass",
      "decor-strawberry-molds",
      "urn-glass",
      "pomegranate_glass_red_gold_stem_sticker",
      "dish_jadeite_glass_rabbit_on_nest_covered_sticker",
    ],
  },
  {
    key: "candles",
    name: "Candles",
    emoji: "🕯️",
    items: [
      "candle-pillar",
      "candle-rose-portrait",
      "candles-bottles",
      "decor-soy-candle",
      "decor-taper-candles",
      "candlestick_ornate_drip_candle_mint_green_sticker",
      "candlestick_glass_bubble_stem_green_taper_ivory_sticker",
      "candlestick_glass_olive_green_lit_ivory_candle_left_sticker",
      "candlestick_glass_olive_green_lit_ivory_candle_right_sticker",
      "bottle_candle_holder_dripping_wax_cobalt_blue_sticker",
      "bottle_candle_holder_embossed_dripping_wax_red_pink_sticker",
    ],
  },
  {
    key: "angel-candles",
    name: "Angel & cupid candles",
    emoji: "👼",
    items: [
      "candle-cherub",
      "decor-angel-candle",
      "decor-angel-candle-lilac",
      "decor-cupid-candle-blue",
      "decor-cupid-candle-pink",
      "candle_cherub_angel_seated_pastel_blue_sticker",
      "candle_cherub_angel_seated_mint_green_sticker",
    ],
  },
  {
    key: "lamps",
    name: "Lamps",
    emoji: "🪔",
    items: [
      "lamp-porcelain",
      "lamp_aladdin_genie_pink_enamel_gold_jeweled_sticker",
      "lamp_bankers_desk_green_glass_shade_brass_pull_chain_sticker",
    ],
  },
  {
    key: "vanity",
    name: "Perfume",
    emoji: "🧴",
    items: [
      "perfume-collection",
      "perfume-cloud",
      "perfume-daisy",
      "perfume-flora",
      "perfume-butterfly",
      "decor-flower-perfume",
      "decor-perfume-amber",
      "decor-perfume-bow",
      "decor-perfume-dreamland",
      "decor-perfume-pink",
      "bottle_perfume_crystal_stopper_enamel_floral_purple_vintage_sticker",
      "cup_vanity_pressed_glass_amber_perfume_tubes_sticker",
    ],
  },
  {
    key: "vanity-table",
    name: "Vanity",
    emoji: "💄",
    items: [
      "brushes",
      "brush_makeup_rose_enamel_leaves_gold_sticker",
      "mirror-tabletop",
      "mirror-hand",
      "tissue-holder",
      "decor-tissue-box-blue",
    ],
  },
  {
    key: "bath",
    name: "Bath & linens",
    emoji: "🛁",
    items: [
      "decor-fruit-towels",
      "decor-strawberry-towel",
      "decor-heart-towels",
      "decor-strawberry-soaps",
      "caddy_shower_hanging_wire_three_tier_blue_sticker",
    ],
  },
  {
    key: "bottles",
    name: "Bottles & jars",
    emoji: "🍾",
    items: [
      "flask-horseshoe",
      "wine_la_boheme_act_one_yarra_valley_white_bottle_sticker",
      "wine_la_boheme_act_two_yarra_valley_rose_bottle_sticker",
      "bottle_glass_jar_embossed_aqua_vintage_sticker",
      "bottle_glass_gin_sealed_green_vintage_sticker",
      "bottle_glass_embossed_medallion_dusty_rose_pink_sticker",
      "bottle_dropper_apothecary_glass_emerald_green_sticker",
      "jar_trinket_cut_glass_lidded_blush_pink_sticker",
      "decor-pink-glass-jar",
      "decor-green-glass-insulator",
      "canister_jadeite_glass_lidded_cocoa_sticker",
      "canister_jadeite_glass_lidded_sugar_sticker",
      "canister_jadeite_glass_lidded_rosemary_sticker",
    ],
  },
  {
    key: "misters",
    name: "Plant misters",
    emoji: "💦",
    items: [
      "decor-plant-mister-blue",
      "decor-plant-mister-mint",
      "decor-plant-mister-pink",
      "decor-glass-mister",
      "mister_plant_spray_bottle_embossed_glass_ombre_turquoise_pink_antique_sticker",
    ],
  },
  {
    key: "keepsakes",
    name: "Keepsakes",
    emoji: "🗝️",
    items: [
      "road_case_vintage_mail_stack_black_sticker",
      "music-box-egg",
      "egg-cobalt",
      "purse-porcelain",
      "medallion-lady",
      "cameo-jasperware",
      "decor-pocket-watch",
      "tin_tarot_golden_art_nouveau_sticker",
    ],
  },
  {
    key: "trinket-boxes",
    name: "Trinket boxes",
    emoji: "🎁",
    items: [
      "trinket-box",
      "box_trinket_round_portrait_cream_gold_vintage_sticker",
      "box_trinket_jasperware_cameo_powder_blue_sticker",
      "box_trinket_octagonal_cobalt_blue_gold_roses_sticker",
      "decor-pink-gilt-box",
      "decor-heart-gift-box",
      "tea-caddy",
      "box_incense_burner_wood_lattice_moon_stars_brass_sticker",
      "box_incense_burner_wood_lattice_moon_stars_upright_sticker",
    ],
  },
  {
    key: "desert",
    name: "Desert",
    emoji: "🌵",
    items: [
      "jackrabbit_desert_black_tailed_standing_sticker",
      "tortoise_desert_walking_sticker",
      "fox_kit_desert_standing_sticker",
      "roadrunner_desert_running_sticker",
      "ocotillo_agave_cluster_flowering_red_sticker",
      "cactus_golden_barrel_agave_cluster_sticker",
      "cactus_prickly_pear_cluster_fruiting_sticker",
      "cactus_saguaro_cholla_desert_cluster_sticker",
    ],
  },
  { key: "characters", name: "Characters", emoji: "👤", items: ["character_molly_sticker", "character_kate_sticker", "character_brea_sticker", "character_leonardo_sticker", "character_madeleine_sticker", "character_moselle_sticker", "character_kayenat_sticker", "character_ella_sticker", "character_sarah_sticker", "character_stephanie_sticker", "character_jonathan_sticker", "character_dream_sticker", "character_bryan_sticker", "character_jessica_sticker", "character_peter_sticker", "character_jordan_sticker", "character_destiny_sticker", "character_jericha_sticker", "character_jerusha_sticker", "character_alisha_sticker", "character_jeannette_sticker", "character_chade_sticker"] },
  { key: "tech", name: "Tech", emoji: "💻", items: ["computer_imac_purple_back_view_sticker", "computer_imac_g3_lime_green_keyboard_mouse_sticker", "computer_imac_g3_lime_green_front_facing_sticker", "sticker_maker_create_a_sticker_mini_pink_lime_sticker"] },
  { key: "games", name: "Games", emoji: "🎱", items: ["pool_table_retro_walnut_green_felt_sticker"] },
  { key: "music", name: "Music", emoji: "🎹", items: ["synthesizer_keyboard_yamaha_cream_sticker"] },
  // the rest of the Temu room decor, not on any shelf above (filled in below)
  { key: "decor", name: "Room decor", emoji: "🏺", items: [] },
  /* everything else in the room — the gilded medallion, the jasperware and
     the woman at her lyre land here rather than in Artwork */
  { key: "trinkets", name: "Trinkets", emoji: "💎", items: [] },
];

/* Room decor keeps only the decor that hasn't got a shelf of its own */
{
  const elsewhere = new Set(SHELVES.filter((s) => s.key !== "decor").flatMap((s) => s.items as string[]));
  SHELVES.find((s) => s.key === "decor")!.items = DECOR.filter((id) => !elsewhere.has(id));
}

/** what starts out of the room: the blossoms, the teacups, the pillows and
    the frisbee dog, as Molly left it; everything else starts in it */
export const HIDDEN_AT_FIRST: string[] = [
  ...["blossoms", "teacups", "pillows"].flatMap((key) => SHELVES.find((s) => s.key === key)!.items),
  "dog-frisbee",
];

/** each shelf's items that are actually in this room, trinkets catching the rest */
export function stock(inRoom: string[]): { shelf: Shelf; items: string[] }[] {
  const named = new Set(SHELVES.flatMap((s) => s.items as string[]));
  // the shelves made at Brat Academy's sticker maker (madeStickers.ts), after
  // the code's own, and the made stickers on whichever shelf they were put on
  const all: Shelf[] = [...SHELVES, ...madeShelves().map((m) => ({ ...m, items: [] as PropId[] }))];
  return all.map((shelf) => ({
    shelf,
    items:
      shelf.key === "trinkets"
        ? inRoom.filter((id) => !named.has(id) && !isMade(id))
        : [...(shelf.items as string[]), ...madeOn(shelf.key)].filter((id) => inRoom.includes(id)),
  })).filter((s) => s.items.length > 0);
}

/** every shelf a made sticker can go on: the code's, then the made ones
    (not Trinkets, which only catches what has no shelf) */
export const shelvesToFileOn = (): { key: string; name: string; emoji: string }[] => [
  ...SHELVES.filter((s) => s.key !== "trinkets").map(({ key, name, emoji }) => ({ key, name, emoji })),
  ...madeShelves(),
];
